/**
 * Location Detail PDF Generator
 *
 * Generates a formatted PDF for a location detail page with three sections:
 *   1. Practice/Clinic Details  – location address, phone, email
 *   2. Provider Details         – all providers at this location (with a Notes row per provider)
 *   3. Staff Details            – contacts at this location
 *
 * Reuses the existing Doctivity gradient-header template
 * (drawPageHeader / drawContinuationPage / loadDoctivityLogo).
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { PhoneUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { registerRobotoFont } from './robotoFont';
import { drawPageHeader, drawContinuationPage } from './chartRenderer';
import { loadDoctivityLogo } from './logoManager';
import axiosInstance from 'utils/axiosUtil';

// ─── Module-level icon cache ─────────────────────────────────────────────────
let cachedHubIcon = null;

/**
 * Renders the Material Icons 'hub' glyph onto a canvas and returns a data URL.
 * Works because the Material Icons webfont is already loaded by the app.
 * Cached after the first call.
 */
const loadHubIcon = async (color = '#2082F4', size = 32) => {
  if (cachedHubIcon) return cachedHubIcon;

  await document.fonts.ready;

  const scale = 2; // render at 2× for crisp display in PDF
  const canvas = document.createElement('canvas');
  canvas.width  = size * scale;
  canvas.height = size * scale;
  const ctx = canvas.getContext('2d');

  ctx.font          = `${size * scale}px "Material Icons"`;
  ctx.fillStyle     = color;
  ctx.textBaseline  = 'middle';
  ctx.textAlign     = 'center';
  ctx.fillText('hub', (size * scale) / 2, (size * scale) / 2);

  cachedHubIcon = canvas.toDataURL('image/png');
  return cachedHubIcon;
};

// ─── Layout constants ────────────────────────────────────────────────────────
const LOGO_MAX_W    = 90;
const LOGO_MAX_H    = 22;
const LOGO_ASPECT   = 261 / 59;      // native logo dimensions
const CONTENT_L     = 20;            // left inset inside the white container
const CONTENT_R     = 20;            // right inset
const SECTION_GAP   = 14;            // vertical gap between sections
const FOOTER_RESERVE = 45;           // reserve at page bottom for gradient footer
const TABLE_MARGIN_TOP = 60;         // autoTable top-margin on continuation pages

// ─── Color palette ───────────────────────────────────────────────────────────
const SECTION_BG    = [217, 217, 217];
const SECTION_TEXT  = [0,   0,   0  ];
const BORDER        = [130, 130, 130];
const COL_HEAD_BG   = [255, 255, 255];
const COL_HEAD_TXT  = [0,   0,   0  ];
const NOTES_BG      = [249, 249, 249];
const NOTES_TXT     = [160, 160, 160];

// ─── Private helpers ─────────────────────────────────────────────────────────

/** Place the cached logo at the top-right corner of the current page. */
const placeLogoOnPage = (doc, logo, pageWidth) => {
  if (!logo) return;
  let w = LOGO_MAX_W;
  let h = w / LOGO_ASPECT;
  if (h > LOGO_MAX_H) { h = LOGO_MAX_H; w = h * LOGO_ASPECT; }
  const lx = pageWidth - 15 - LOGO_MAX_W;
  doc.addImage(
    `data:image/${logo.type.toLowerCase()};base64,${logo.data}`,
    logo.type,
    lx, 5, w, h,
  );
};

/**
 * Draw a gray section-header bar (e.g. "Provider Details").
 * Returns the Y position immediately below the bar.
 */
const drawSectionHeader = (doc, title, x, y, width) => {
  const h = 18;
  doc.setFillColor(...SECTION_BG);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.rect(x, y, width, h, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...SECTION_TEXT);
  doc.text(title, x + 6, y + 12);
  return y + h;
};

/**
 * Draw the location key-value grid (Practice/Clinic Details table).
 * Returns the Y position immediately below the last row.
 */
const drawLocationGrid = (doc, loc, x, y, width) => {
  const rowH   = 16;
  const labelW = 105;

  const border = () => {
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
  };

  const drawCell = (cx, cy, cw) => {
    border();
    doc.rect(cx, cy, cw, rowH, 'S');
  };

  const drawDivider = (dx, dy) => {
    border();
    doc.line(dx, dy, dx, dy + rowH);
  };

  const writeLabel = (txt, cx, cy) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(txt, cx + 4, cy + 11);
  };

  const writeValue = (txt, cx, cy, maxW) => {
    if (!txt) return;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    let s = String(txt);
    while (doc.getTextWidth(s) > maxW - 8 && s.length > 6) {
      s = `${s.slice(0, -4)}…`;
    }
    doc.text(s, cx + 4, cy + 11);
  };

  /** Full-width two-column row (label | value). */
  const fullRow = (lbl, val, cy) => {
    drawCell(x, cy, width);
    drawDivider(x + labelW, cy);
    writeLabel(lbl, x, cy);
    writeValue(val, x + labelW, cy, width - labelW);
    return cy + rowH;
  };

  /** Two side-by-side half-rows. */
  const halfRows = (lbl1, val1, lbl2, val2, cy) => {
    const hw = width / 2;
    drawCell(x, cy, hw);
    drawDivider(x + labelW, cy);
    writeLabel(lbl1, x, cy);
    writeValue(val1, x + labelW, cy, hw - labelW);

    drawCell(x + hw, cy, hw);
    drawDivider(x + hw + labelW, cy);
    writeLabel(lbl2, x + hw, cy);
    writeValue(val2, x + hw + labelW, cy, hw - labelW);
    return cy + rowH;
  };

  let cy = y;

  cy = fullRow('Clinic Name:', loc.name, cy);

  const street = [loc.address1, loc.address2].filter(Boolean).join(' ');
  cy = fullRow('Street:', street, cy);

  cy = halfRows('City:', loc.city, 'State:', loc.state, cy);
  cy = fullRow('Postal Code:', loc.postal_code, cy);

  const phone = PhoneUtil.formatNumber(loc.phone) || '';
  const fax   = PhoneUtil.formatNumber(loc.fax)   || '';
  cy = halfRows('Phone:', phone, 'Fax:', fax, cy);

  cy = fullRow('Email:', loc.email, cy);

  return cy;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Build the jsPDF document for a location.
 *
 * @param {Object} locationData  - Location record from the store
 * @param {Array}  providers     - All provider rows for this location
 * @param {Array}  contacts      - All contact rows for this location
 * @returns {Promise<jsPDF>}
 */
export const generateLocationPDF = async (locationData, providers, contacts) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  registerRobotoFont(doc);

  const pageWidth  = doc.internal.pageSize.width;   // 612 pt
  const pageHeight = doc.internal.pageSize.height;  // 792 pt

  // Pre-load logo once; reused synchronously in autoTable's didAddPage hook
  const logo     = await loadDoctivityLogo();
  const placeLogo = () => placeLogoOnPage(doc, logo, pageWidth);

  // ── Page 1: gradient header ────────────────────────────────────────────────
  const title            = locationData.name || 'Location Details';
  const { y: contentY }  = drawPageHeader(doc, title, '', [], { pdfType: 'printable' });
  placeLogo();

  let y  = contentY;
  const lx = CONTENT_L;
  const cw = pageWidth - CONTENT_L - CONTENT_R;

  /**
   * Ensure at least `neededH` points are available before the footer area.
   * Rolls to a new continuation page if not.
   */
  const needSpace = (neededH) => {
    if (y + neededH > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      y = drawContinuationPage(doc, { pdfType: 'printable' });
      placeLogo();
    }
  };

  // Shared autoTable options applied to every table in this PDF
  const tableDefaults = {
    styles: {
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
      lineColor: BORDER,
      lineWidth: 0.3,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: COL_HEAD_BG,
      textColor: COL_HEAD_TXT,
      fontStyle: 'bold',
      lineColor: BORDER,
      lineWidth: 0.3,
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
    },
    // On pages 2+: draw the continuation-page gradient background and logo
    didAddPage: () => {
      drawContinuationPage(doc, { pdfType: 'printable' });
      placeLogo();
    },
    margin: { left: lx, right: CONTENT_R, top: TABLE_MARGIN_TOP },
  };

  // ── 1. Practice / Clinic Details ──────────────────────────────────────────
  needSpace(18 + 6 * 16); // section header + 6 detail rows
  y = drawSectionHeader(doc, 'Location Details', lx, y, cw);
  y = drawLocationGrid(doc, locationData, lx, y, cw);
  y += SECTION_GAP;

  // ── 2. Provider Details ───────────────────────────────────────────────────
  if (providers.length > 0) {
    needSpace(18 + 50); // header bar + column header + at least one data row

    y = drawSectionHeader(doc, 'Provider Details', lx, y, cw);

    // Pre-render the Material Icons hub glyph using the webfont already loaded in the browser
    const hubIconDataUrl = await loadHubIcon();

    // Interleave data rows with blank notes rows for handwritten annotations
    const provBody = [];
    providers.forEach((p) => {
      const name      = [p.first_name, p.last_name].filter(Boolean).join(' ');
      const specialty = SpecialtyUtil.getNameFromTaxonomy(p.taxonomy) || '';
      const phone     = PhoneUtil.formatNumber(p.phone) || '';
      const mobile    = PhoneUtil.formatNumber(p.mobile_phone) || '';
      // Col 0 is blank text – hub icon drawn via didDrawCell for affiliated providers
      provBody.push(['', name, p.credential || '', specialty, p.npi || '', p.email || '', phone, mobile]);
      // Notes row spans all 8 columns so "Notes:" aligns flush-left with the rest of the table
      provBody.push([{ content: 'Notes:', colSpan: 8 }]);
    });

    autoTable(doc, {
      ...tableDefaults,
      startY: y,
      head: [['', 'Name', 'Credential', 'Specialty', 'NPI', 'Email', 'Phone', 'Mobile']],
      body: provBody,
      columnStyles: {
        0: { cellWidth: 22, halign: 'center' }, // Affiliated icon column
        1: { cellWidth: 93  },                  // Name
        2: { cellWidth: 52  },                  // Credential
        3: { cellWidth: 78  },                  // Specialty
        4: { cellWidth: 58  },                  // NPI
        5: { cellWidth: 'auto' },               // Email (takes remaining space)
        6: { cellWidth: 66  },                  // Phone
        7: { cellWidth: 66  },                  // Mobile
      },
      didParseCell: (data) => {
        const isNotesRow = data.section === 'body' && data.row.index % 2 === 1;

        // Notes row: single colSpan-8 cell — give it full borders and notes styling
        if (isNotesRow) {
          Object.assign(data.cell.styles, {
            fillColor:     NOTES_BG,
            textColor:     NOTES_TXT,
            fontSize:      7,
            fontStyle:     'italic',
            minCellHeight: 20,
            halign:        'left',
            lineWidth:     { top: 0.3, bottom: 0.3, left: 0.3, right: 0.3 },
          });
          return; // skip the column-divider logic below for notes rows
        }

        // Data + header rows: remove the divider between icon column and Name column
        if (data.column.index === 0) {
          data.cell.styles.lineWidth = { top: 0.3, bottom: 0.3, left: 0.3, right: 0 };
        }
        if (data.column.index === 1) {
          data.cell.styles.lineWidth = { top: 0.3, bottom: 0.3, left: 0, right: 0.3 };
        }
      },
      didDrawCell: (data) => {
        // Place the hub icon image for affiliated providers (even body rows, col 0 only)
        if (data.section !== 'body' || data.column.index !== 0 || data.row.index % 2 !== 0) return;
        const provider = providers[Math.floor(data.row.index / 2)];
        if (!provider?.clients?.length || !hubIconDataUrl) return;

        const iconSize = 11; // pt – sized to fit the narrow column
        const ix = data.cell.x + (data.cell.width - iconSize) / 2;
        const iy = data.cell.y + (data.cell.height - iconSize) / 2;
        doc.addImage(hubIconDataUrl, 'PNG', ix, iy, iconSize, iconSize);
      },
    });

    y = doc.lastAutoTable.finalY + SECTION_GAP;
  } else {
    needSpace(18 + 20);
    y = drawSectionHeader(doc, 'Provider Details', lx, y, cw);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('No providers associated with this location.', lx + 6, y + 14);
    y += 26 + SECTION_GAP;
  }

  // ── 3. Contact Details ────────────────────────────────────────────────────
  needSpace(18 + 50);

  y = drawSectionHeader(doc, 'Contact Details', lx, y, cw);

  if (contacts.length > 0) {
    const contactBody = [];
    contacts.forEach((c) => {
      contactBody.push([
        [c.first_name, c.last_name].filter(Boolean).join(' '),
        c.title || '',
        PhoneUtil.formatNumber(c.phone) || '',
        c.email || '',
      ]);
      contactBody.push([{ content: 'Notes:', colSpan: 4 }]);
    });

    autoTable(doc, {
      ...tableDefaults,
      startY: y,
      head: [['Name', 'Title', 'Phone', 'Email']],
      body: contactBody,
      columnStyles: {
        0: { cellWidth: 130 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 90  },
        3: { cellWidth: 150 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index % 2 === 1) {
          Object.assign(data.cell.styles, {
            fillColor:     NOTES_BG,
            textColor:     NOTES_TXT,
            fontSize:      7,
            fontStyle:     'italic',
            minCellHeight: 20,
            halign:        'left',
          });
        }
      },
    });

    y = doc.lastAutoTable.finalY;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('No contacts associated with this location.', lx + 6, y + 14);
    y += 26;
  }

  return doc;
};

// ─── Data-fetching helpers ────────────────────────────────────────────────────
// Mirrors the pattern used in LocationProviders.downloadProviders / LocationContacts.downloadContacts

/**
 * Fetch all providers at a location (no pagination limit — up to 5 000 rows).
 */
export const fetchAllLocationProviders = async (locationId, clientId) => {
  const response = await axiosInstance.get('/Providers', {
    params: {
      opts: {
        where: {},
        attributes: ['id', 'first_name', 'last_name', 'credential', 'npi', 'phone', 'mobile_phone', 'email'],
        include: [
          {
            association: 'location',
            where: { id: locationId },
            required: true,
            attributes: [],
          },
          {
            association: 'taxonomy',
            attributes: ['id', 'classification', 'specialization', 'doctivity_name'],
          },
          {
            association: 'clients',
            attributes: ['id'],
            required: false,
            duplicating: false,
            through: {
              attributes: [],
              where: { client_id: clientId, is_affiliated: true },
            },
          },
        ],
        order: [['last_name', 'ASC']],
        limit: 5000,
        offset: 0,
      },
    },
    timeout: 60000,
  });
  return response.data?.rows || [];
};

/**
 * Fetch all contacts at a location (no pagination limit — up to 5 000 rows).
 */
export const fetchAllLocationContacts = async (locationId, clientId) => {
  const response = await axiosInstance.get('/Contacts', {
    params: {
      opts: {
        where: { client_id: clientId },
        attributes: ['id', 'first_name', 'last_name', 'title', 'phone', 'email'],
        include: [
          {
            association: 'location',
            where: { id: locationId },
            required: true,
            attributes: [],
          },
        ],
        order: [['last_name', 'ASC']],
        limit: 5000,
        offset: 0,
      },
    },
    timeout: 60000,
  });
  return response.data?.rows || [];
};

/**
 * Full download pipeline:
 *   fetch providers + contacts → generate PDF → trigger browser download.
 *
 * @param {Object} locationData  - Location record (needs at minimum .id and .name)
 * @param {number} clientId      - Active client ID (for tenant-scoped contact fetch)
 */
export const downloadLocationPDF = async (locationData, clientId) => {
  const [providers, contacts] = await Promise.all([
    fetchAllLocationProviders(locationData.id, clientId),
    fetchAllLocationContacts(locationData.id, clientId),
  ]);

  const doc = await generateLocationPDF(locationData, providers, contacts);

  const safeName  = (locationData.name || `location_${locationData.id}`)
    .replace(/[^a-zA-Z0-9]/g, '_');
  const dateSuffix = format(new Date(), 'yy_MM_dd');
  doc.save(`doctivity_location_${safeName}_${dateSuffix}.pdf`);
};

export default {
  generateLocationPDF,
  downloadLocationPDF,
  fetchAllLocationProviders,
  fetchAllLocationContacts,
};
